/**
 * Same-origin SFRA controller URLs for browser-side calls.
 *
 * Lifted from `app/hec/config/apple-pay.ts` (which re-exports
 * `buildSameOriginSfraUrl` for its existing importers) so the Insiders claim
 * form and future SFRA bridges share one builder instead of a fifth copy.
 *
 * The instance path (`/on/demandware.store/Sites-<id>-Site/default/<Action>`)
 * is mandatory: pretty aliases are not edge-routed and loop back into the
 * Worker (CF 1101), and bare `/Controller-Action` paths 404 on dev. Same-origin
 * is mandatory too — SFRA sends no CORS headers, so the request has to ride the
 * hybrid proxy on the current host, which also carries the `dwsid` + SLAS
 * cookies the controllers need.
 */

const SFRA_INSTANCE_PATH =
  /^\/on\/demandware\.store\/Sites-[A-Za-z0-9_-]+-Site\//;

/** Relative instance path for one controller action on one site. */
export const buildSfraControllerPath = (
  siteId: string,
  action: string,
): string => `/on/demandware.store/Sites-${siteId}-Site/default/${action}`;

/** Absolute same-origin URL for one controller action (needs the page origin). */
export const buildSameOriginSfraUrl = (
  origin: string,
  siteId: string,
  action: string,
): string =>
  new URL(buildSfraControllerPath(siteId, action), origin).toString();

/**
 * True only for a RELATIVE SFRA instance path. An absolute URL fails on
 * purpose: a server-built `actionUrl` that names another host would make the
 * browser POST cross-origin, without the session cookies SFRA needs.
 */
export const isSameOriginSfraPath = (candidate: string): boolean =>
  SFRA_INSTANCE_PATH.test(candidate);
